import type {
  IntakeItem,
  IntakeItemRecord,
  IntakeItemStatus,
  IntakeProvenance,
  IntakeRequirementDefinition,
  IntakeServiceDefinition,
  IntakeServiceId,
  IntakeServiceSummary,
} from './types';

const COMPLETE_STATUSES = new Set<IntakeItemStatus>(['accepted', 'expiring', 'staff_complete']);
const CLOSED_STATUSES = new Set<IntakeItemStatus>(['accepted', 'staff_complete', 'not_applicable']);
const NEEDS_INPUT_STATUSES = new Set<IntakeItemStatus>([
  'missing',
  'ai_flagged',
  'needs_attention',
]);
const PROGRESS_EXCLUDED_STATUSES = new Set<IntakeItemStatus>(['not_applicable']);
const ITEM_STATUS_SORT_ORDER: Record<IntakeItemStatus, number> = {
  needs_attention: 0,
  ai_flagged: 1,
  missing: 2,
  submitted: 3,
  in_review: 4,
  staff_in_progress: 5,
  expiring: 6,
  staff_complete: 7,
  accepted: 8,
  not_applicable: 9,
};

export function statusCountsTowardProgress(status: IntakeItemStatus) {
  return !PROGRESS_EXCLUDED_STATUSES.has(status);
}

export function statusCountsAsComplete(status: IntakeItemStatus) {
  return COMPLETE_STATUSES.has(status);
}

export function itemIsOpen(item: IntakeItem) {
  return !CLOSED_STATUSES.has(item.status);
}

export function itemNeedsClinicianInput(item: IntakeItem) {
  return item.owner !== 'jotpsych' && NEEDS_INPUT_STATUSES.has(item.status);
}

export function itemIsOnFile(item: IntakeItem) {
  return !itemNeedsClinicianInput(item) && item.status !== 'not_applicable';
}

export function getProgressForService(items: IntakeItem[]) {
  const countedItems = items.filter(
    item => item.owner !== 'jotpsych' && statusCountsTowardProgress(item.status),
  );

  if (countedItems.length === 0) {
    return 0;
  }

  const completeItems = countedItems.filter(item => statusCountsAsComplete(item.status));
  return Math.round((completeItems.length / countedItems.length) * 100);
}

export function splitProfileItems(items: IntakeItem[]) {
  return {
    neededItems: items.filter(itemNeedsClinicianInput),
    onFileItems: items.filter(itemIsOnFile),
  };
}

export function sortIntakeItemsByStatus(items: IntakeItem[]) {
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const statusDelta =
        ITEM_STATUS_SORT_ORDER[a.item.status] - ITEM_STATUS_SORT_ORDER[b.item.status];
      return statusDelta || a.index - b.index;
    })
    .map(({ item }) => item);
}

export function toIntakeItem(
  item: IntakeItemRecord,
  requirement: IntakeRequirementDefinition,
): IntakeItem {
  const extractedFromAi = item.sourceRefs.some(
    sourceRef => sourceRef.relationship === 'extracted_from',
  );
  const sourceSystemPrefill = item.sourceRefs.some(sourceRef =>
    ['provider_billing_info', 'jotcred_provider', 'billing_entity', 'payer'].includes(
      sourceRef.type,
    ),
  );
  const manualSource = item.sourceRefs.some(sourceRef => sourceRef.type === 'manual_entry');
  const provenance: IntakeProvenance | undefined = extractedFromAi
    ? 'ai'
    : sourceSystemPrefill
      ? 'source_system'
      : manualSource
        ? 'manual'
        : undefined;

  return {
    id: item.intakeItemId,
    intakeItemId: item.intakeItemId,
    requirementKey: item.requirementKey,
    conceptKey: item.conceptKey ?? undefined,
    label: requirement.title,
    description: requirement.description,
    status: item.status,
    type: item.itemType,
    owner: item.owner,
    subjectType: item.subjectType,
    subjectRef: item.subjectRef,
    serviceIds: item.serviceKey ? [item.serviceKey] : [],
    sourceRefs: item.sourceRefs,
    evidenceSummary: item.evidenceSummary,
    metadata: requirement.metadata ?? {},
    provenance,
    actionLabel:
      typeof item.metadata.actionLabel === 'string' ? item.metadata.actionLabel : undefined,
    instruction:
      typeof item.metadata.instruction === 'string' ? item.metadata.instruction : undefined,
    issueNote: typeof item.metadata.issueNote === 'string' ? item.metadata.issueNote : undefined,
    updatedAtLabel:
      typeof item.metadata.updatedAtLabel === 'string' ? item.metadata.updatedAtLabel : undefined,
  };
}

export function toServiceSummaries(
  services: IntakeServiceDefinition[],
  items: IntakeItem[],
  activeServiceIds: IntakeServiceId[],
): IntakeServiceSummary[] {
  return services
    .filter(service => service.status === 'active')
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((service, index) => {
      const serviceItems = items.filter(item => item.serviceIds.includes(service.serviceKey));
      return {
        id: service.serviceKey,
        label: service.label,
        description: service.description,
        progress: getProgressForService(serviceItems),
        active: activeServiceIds.includes(service.serviceKey),
        accent: index === 1 ? 'secondary' : index === 2 ? 'warning' : 'primary',
      };
    });
}
